// src/components/ui/select.tsx - Fixed version with proper scrolling
import { cn } from "@/utils"
import * as React from "react"

export interface SelectProps {
    children: React.ReactNode
    value?: string
    onValueChange?: (value: string) => void
    disabled?: boolean
}

const Select = ({ children, value, onValueChange, disabled = false }: SelectProps) => {
    const [isOpen, setIsOpen] = React.useState(false)

    const handleValueChange = (newValue: string) => {
        onValueChange?.(newValue)
        setIsOpen(false)
    }

    // Find the selected option to display its label
    const getSelectedLabel = (): React.ReactNode => {
        let selectedLabel: React.ReactNode = null

        React.Children.forEach(children, child => {
            if (React.isValidElement(child) && child.type === SelectContent) {
                React.Children.forEach(child.props.children, item => {
                    if (React.isValidElement(item)) {
                        const itemProps = item.props as any
                        if (itemProps.value === value) {
                            selectedLabel = itemProps.children
                        }
                    }
                })
            }
        })

        return selectedLabel
    }

    return (
        <div className="relative">
            {React.Children.map(children, child => {
                if (!React.isValidElement(child)) return child

                if (child.type === SelectTrigger) {
                    return React.cloneElement(child as React.ReactElement<any>, {
                        onClick: () => !disabled && setIsOpen(!isOpen),
                        disabled,
                        isOpen,
                        selectedLabel: getSelectedLabel()
                    })
                }
                if (child.type === SelectContent) {
                    return isOpen && !disabled ? React.cloneElement(child as React.ReactElement<any>, {
                        onValueChange: handleValueChange,
                        onClose: () => setIsOpen(false),
                        selectedValue: value
                    }) : null
                }
                return child
            })}
        </div>
    )
}

export interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isOpen?: boolean
    selectedLabel?: React.ReactNode
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(({
    className,
    children,
    onClick,
    disabled,
    isOpen,
    selectedLabel,
    ...props
}, ref) => {
    return (
        <button
            ref={ref}
            className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            onClick={onClick}
            disabled={disabled}
            type="button"
            {...props}
        >
            <span className="block truncate text-left">
                {selectedLabel || (
                    React.Children.map(children, child => {
                        if (React.isValidElement(child) && child.type === SelectValue) {
                            return child.props.placeholder
                        }
                        return child
                    })
                )}
            </span>
            <svg
                className={cn("h-4 w-4 opacity-50 transition-transform flex-shrink-0", isOpen && "rotate-180")}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        </button>
    )
})
SelectTrigger.displayName = "SelectTrigger"

export interface SelectValueProps {
    placeholder?: string
}

const SelectValue = (_props: SelectValueProps) => {
    // This component is just for providing placeholder text
    // The actual value display is handled in SelectTrigger
    return null
}
SelectValue.displayName = "SelectValue"

export interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
    onValueChange?: (value: string) => void
    onClose?: () => void
    selectedValue?: string
    position?: string
    side?: string
    align?: string
    sideOffset?: number
    avoidCollisions?: boolean
    collisionPadding?: number
    sticky?: string
}

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(({
    className,
    children,
    onValueChange,
    onClose,
    selectedValue,
    // Filter out positioning props that shouldn't go to DOM
    position: _position,
    side: _side,
    align: _align,
    sideOffset: _sideOffset,
    avoidCollisions: _avoidCollisions,
    collisionPadding: _collisionPadding,
    sticky: _sticky,
    ...props
}, _ref) => {
    const contentRef = React.useRef<HTMLDivElement>(null)
    const [dimensions, setDimensions] = React.useState({ maxHeight: '15rem' }) // Default 60 (15rem)

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
                onClose?.()
            }
        }

        // Calculate optimal max-height based on viewport
        const calculateMaxHeight = () => {
            const viewportHeight = window.innerHeight
            const triggerRect = contentRef.current?.parentElement?.getBoundingClientRect()

            if (triggerRect) {
                const spaceBelow = viewportHeight - triggerRect.bottom - 20 // 20px padding
                const spaceAbove = triggerRect.top - 20
                const availableSpace = Math.max(spaceBelow, spaceAbove)

                // Use up to 50% of viewport height or available space, whichever is smaller
                const maxHeight = Math.min(viewportHeight * 0.5, availableSpace, 400) // Max 400px
                setDimensions({ maxHeight: `${maxHeight}px` })
            }
        }

        calculateMaxHeight()
        window.addEventListener('resize', calculateMaxHeight)
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            window.removeEventListener('resize', calculateMaxHeight)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [onClose])

    // Extract the explicit maxHeight from className if provided
    const getMaxHeightFromClassName = (className?: string): string | null => {
        const maxHeightMatch = className?.match(/max-h-\[([^\]]+)\]/)
        return maxHeightMatch ? maxHeightMatch[1] : null
    }

    const customMaxHeight = getMaxHeightFromClassName(className)
    const finalMaxHeight = customMaxHeight || dimensions.maxHeight

    return (
        <div
            ref={contentRef}
            className={cn(
                "absolute top-full z-[9999] mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg",
                className
            )}
            style={{
                maxHeight: finalMaxHeight,
                zIndex: 9999,
                ...props.style
            }}
            {...props}
        >
            <div
                className="overflow-y-auto overflow-x-hidden"
                style={{ maxHeight: finalMaxHeight }}
            >
                {React.Children.map(children, (child, index) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<any>, {
                            onValueChange,
                            isSelected: child.props?.value === selectedValue,
                            key: child.key || index
                        })
                    }
                    return child
                })}
            </div>
        </div>
    )
})
SelectContent.displayName = "SelectContent"

export interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
    value: string
    onValueChange?: (value: string) => void
    isSelected?: boolean
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(({
    className,
    children,
    value,
    onValueChange,
    isSelected,
    ...props
}, ref) => (
    <div
        ref={ref}
        className={cn(
            "relative flex w-full cursor-pointer select-none items-center py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            isSelected && "bg-accent text-accent-foreground",
            className
        )}
        onClick={() => onValueChange?.(value)}
        role="option"
        aria-selected={isSelected}
        {...props}
    >
        {isSelected && (
            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
            </span>
        )}
        <span className="block truncate">{children}</span>
    </div>
))
SelectItem.displayName = "SelectItem"

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
